import { useCallback, useEffect, useState } from "react";
import { ClipboardText, Copy, Trash, ArrowCounterClockwise, WarningCircle, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

export default function SurveyAnswersPage() {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(() => {
    api.get("/survey-answers").then((r) => setHistory(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (question.trim().length < 10) { toast.error("Paste the full question"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/survey-answers", { question: question.trim() });
      setCurrent(data);
      load();
      toast.success("Answer drafted — review before submitting");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openItem = async (id) => {
    try {
      const { data } = await api.get(`/survey-answers/${id}`);
      setCurrent(data);
      setQuestion(data.question);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this drafted answer?")) return;
    await api.delete(`/survey-answers/${id}`).catch(() => {});
    if (current?.id === id) setCurrent(null);
    load();
  };

  const copyAnswer = () => {
    if (!current) return;
    const text = current.answer;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success("Copied — review the GAPS note before submitting")).catch(() => toast.error("Copy failed"));
    } else {
      toast.error("Copy not available — select the text manually");
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="survey-answers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Survey & RFP Answers</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">
            Paste a surveyor, payer, or RFP question. The answer is drafted in formal policy language from your approved
            knowledge base and policy library — anything not yet covered by your documents is flagged as a gap.
            <strong> Always review before submitting</strong>; delete the internal gap note from what you send.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/reports/policy-notebook`, "_blank")}
          className="shrink-0 border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="policy-notebook-button">
          <FileText size={15} className="mr-1.5" /> Policy Notebook
        </Button>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <Textarea rows={5} value={question} onChange={(e) => setQuestion(e.target.value)}
          placeholder="Paste the question exactly as written — long multi-part questions are fine…"
          className="border-stone-300 bg-white text-sm leading-relaxed" data-testid="survey-question-input" />
        <div className="mt-3 flex justify-end">
          <Button onClick={generate} disabled={busy} className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="survey-generate">
            {busy ? "Drafting… (30-60s)" : "Draft answer"}
          </Button>
        </div>
      </div>

      {current && (
        <div className="rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="survey-answer-card">
          <div className="flex items-center justify-between border-b border-stone-100 px-5 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-400">Draft answer · {String(current.created_at || "").slice(0, 16).replace("T", " ")}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyAnswer} className="border-stone-300 text-xs" data-testid="survey-copy">
                <Copy size={13} className="mr-1" /> Copy
              </Button>
            </div>
          </div>
          {/GAPS TO CLOSE/.test(current.answer) && (
            <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid="survey-gaps-flag">
              <WarningCircle size={16} className="mt-0.5 shrink-0 text-[#8a5d14]" />
              <p className="text-xs text-[#8a5d14]">This answer includes an internal <strong>GAPS TO CLOSE</strong> note at the bottom — items your documents don't cover yet. Remove that section before submitting, and consider closing the gaps.</p>
            </div>
          )}
          <div className="px-5 py-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-800" data-testid="survey-answer-text">{current.answer}</p>
          </div>
          {(current.sources || []).length > 0 && (
            <div className="border-t border-stone-100 px-5 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-400">Grounded in</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {current.sources.map((s) => (
                  <span key={s} className="rounded-full border border-stone-200 bg-accent/40 px-2.5 py-0.5 text-[11px] text-stone-600">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-400">Previous drafts</p>
          <div className="mt-2 space-y-2" data-testid="survey-history">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-4 py-3" data-testid={`survey-history-${h.id}`}>
                <FileText size={16} className="shrink-0 text-stone-300" />
                <p className="min-w-0 flex-1 truncate text-sm text-stone-700">{h.question}</p>
                <span className="shrink-0 text-[11px] text-stone-400">{String(h.created_at || "").slice(0, 10)}</span>
                <button onClick={() => openItem(h.id)} className="shrink-0 text-xs font-semibold text-primary hover:underline" data-testid={`survey-open-${h.id}`}>
                  <ArrowCounterClockwise size={12} className="mr-0.5 inline" /> Open
                </button>
                <button onClick={() => removeItem(h.id)} className="shrink-0 text-stone-300 hover:text-[#C0392B]" data-testid={`survey-delete-${h.id}`}><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
